import { useCallback, useMemo, useState } from "react"

import { Formik } from "formik"
import _, { isEqual } from "lodash"

import { useTranslation } from "@l2r-front/l2r-i18n"
import { InfoIcon } from "@l2r-front/l2r-icons"
import { Task, TechniqueImpact } from "@l2r-front/l2r-proptypes"
import {
    AutocompleteRenderInputParams,
    L2RButton,
    Divider,
    MenuItem,
    ScrollableLayout,
    Typography
} from "@l2r-front/l2r-ui"
import { formatNumber, parseFormattedNumber } from "@l2r-front/l2r-utils"

import { I18N_NAMESPACE } from "../../../../common/constants/i18n"
import { TaskImpactBadge } from "../../components/TaskImpactBadge"
import { MINIMAL_IMPACT, impacts, impactsOrder, otherTaskCodePrefix } from "../../constants/impacts"
import { roadworksQuantityUnits } from "../../constants/units"

import { validationSchema } from "./RoadworkTaskForm.schema"
import * as Styled from "./RoadworkTaskForm.styled"

type TaskInfo = {
    code: string
    name: string
    impact: TechniqueImpact
}

interface RoadworkTaskFormValues {
    taskType?: string
    quantity?: string
    unit?: string
    cost?: string
    comment?: string
}

type RoadworkTaskFormProps = {
    className?: string
    disableTaskTypes: string[]
    index: number
    isSubmitting?: boolean
    onCancel: () => void
    onChange: (inputName: string, newValues: RoadworkTaskFormValues) => void
    onSubmit: (formValues) => void
    localizedTasksAllowed?: boolean
    task?: Task
    projectTasksInfo: Array<TaskInfo>
    tasksInfo: Array<TaskInfo>
}

export function RoadworkTaskForm(props: RoadworkTaskFormProps) {

    const { t } = useTranslation(I18N_NAMESPACE)

    const {
        className,
        disableTaskTypes,
        isSubmitting = false,
        onCancel,
        onChange,
        onSubmit,
        localizedTasksAllowed = true,
        task,
        projectTasksInfo,
        tasksInfo,
    } = props

    const [remainingTasksDisplayed, setRemainingTasksDisplayed] = useState(false)

    const handleInputChange = useCallback((inputName: string, inputValue: string, previousvalues: RoadworkTaskFormValues) => {
        const newValues = {
            ...previousvalues,
            [inputName]: inputValue,
        }
        onChange(inputName, newValues)
    }, [onChange])

    const handleNumberValueChange = useCallback((inputName: string, inputValue: string, previousvalues: RoadworkTaskFormValues, defaultValue: string, setFieldValue: (key: string, value: any) => void) => {
        const inputValueNormalized = parseFormattedNumber(inputValue)
        if (!inputValueNormalized) {
            setFieldValue(inputName, defaultValue)
            handleInputChange(inputName, defaultValue, previousvalues)
        } else if (!inputValueNormalized || inputValueNormalized !== previousvalues[inputName]) {
            if (!inputValueNormalized || inputValueNormalized < 0) {
                setFieldValue(inputName, defaultValue)
                handleInputChange(inputName, defaultValue, previousvalues)
            } else {
                const stringValue = formatNumber(inputValueNormalized, 2)
                setFieldValue(inputName, stringValue)
                handleInputChange(inputName, stringValue, previousvalues)
            }
        }
    }, [handleInputChange])

    const handleUnitChange = useCallback((inputValue: string, previousvalues: RoadworkTaskFormValues) => {
        if (!inputValue) {
            const defaultValue = roadworksQuantityUnits(t)[0]
            handleInputChange("unit", defaultValue, previousvalues)
        }
    }, [handleInputChange, t])

    const impactCategory = useCallback((impact, impactName) => {
        return <Styled.TaskCategory key={`cat-${impact}`}>
            <Typography variant="Desktop & Mobile/Heading/H3">{t(I18N_NAMESPACE, impactName)}</Typography>
            <TaskImpactBadge impact={impact} variant="outlined" />
        </Styled.TaskCategory>
    }, [t])

    const noLTaskInfo = useCallback((key: string) => {
        return <Styled.InfoContainer key={key} severity="default">
            <InfoIcon color="primary" />
            <Styled.InfoText variant="Desktop & Mobile/Body/Small" id={"no-l-task"} key={"no-l-task"}>
                {t(I18N_NAMESPACE, "forms.roadworkTaskForm.noLTasks")}
            </Styled.InfoText>
        </Styled.InfoContainer>
    }, [t])

    const taskTypesOptions = useMemo(() => {
        const remainingTasksInfo = _.differenceBy(tasksInfo, projectTasksInfo, "code")

        const sortTasks = (
            taskA: { code: string; name: string; impact: TechniqueImpact },
            taskB: { code: string; name: string; impact: TechniqueImpact }
        ) => {
            if (taskA.impact !== taskB.impact) {
                return impactsOrder[taskB.impact] - impactsOrder[taskA.impact];
            }
            if (taskA.code.startsWith(otherTaskCodePrefix)) {
                return 1;
            }
            if (taskB.code.startsWith(otherTaskCodePrefix)) {
                return -1;
            }
            return taskA.name.localeCompare(taskB.name);
        };

        const sortedTasksInfo: TaskInfo[] | undefined = projectTasksInfo?.sort(sortTasks)
        const sortedRemainingTasksInfo = remainingTasksInfo?.sort(sortTasks)
        const sortedImpact = Object.entries(impacts).sort((impactA, impactB) => {
            const keyA = impactA[0] as unknown as keyof typeof impactsOrder
            const keyB = impactB[0] as unknown as keyof typeof impactsOrder

            return impactsOrder[keyB] - impactsOrder[keyA];
        })

        return Array.from(sortedImpact).reduce((options: { projectTasks: any[], remainingTasks: any[] }, [impact, impactInfo]) => {
            const impactInt = parseInt(impact)
            const projectTasksOfImpact = sortedTasksInfo?.filter(task => task.impact === impactInt)

            if (projectTasksOfImpact.length > 0) {
                options.projectTasks.push(impactCategory(impactInt, impactInfo.text))
            }
            if (remainingTasksDisplayed && sortedRemainingTasksInfo?.filter(task => task.impact === impactInt).length > 0) {
                options.remainingTasks.push(impactCategory(impactInt, impactInfo.text))
            }
            const remainingTasksOfImpact = sortedRemainingTasksInfo?.filter(task => task.impact === impactInt)
            if (impactInt === MINIMAL_IMPACT && !localizedTasksAllowed) {
                options.projectTasks.push(noLTaskInfo("no-l-project-tasks"))
                if (remainingTasksDisplayed) {
                    options.remainingTasks.pop()
                }
            } else {
                projectTasksOfImpact?.forEach(taskInfo => {
                    options.projectTasks.push(
                        <MenuItem
                            key={taskInfo.code}
                            value={taskInfo.code}
                            disabled={disableTaskTypes.includes(taskInfo.code)}>
                            {taskInfo.name}
                        </MenuItem>)
                })
                remainingTasksOfImpact?.forEach(taskInfo => {
                    options.remainingTasks.push(
                        <MenuItem
                            key={taskInfo.code}
                            value={taskInfo.code}
                            disabled={disableTaskTypes.includes(taskInfo.code)}
                            sx={{ display: remainingTasksDisplayed ? "flex" : "none" }}>
                            {taskInfo.name}
                        </MenuItem>)
                })
            }

            return options
        }, { projectTasks: [], remainingTasks: [] })
    }, [
        projectTasksInfo,
        tasksInfo,
        impactCategory,
        noLTaskInfo,
        localizedTasksAllowed,
        disableTaskTypes,
        remainingTasksDisplayed,
    ])

    const initialValues = useMemo(() => {
        return {
            taskType: task?.taskType,
            quantity: task?.quantity || 0,
            unit: task?.unit || roadworksQuantityUnits(t)[0],
            cost: task?.cost || 0,
            comment: task?.comment,
        }
    }, [task, t])

    return <Formik
        initialValues={initialValues}
        onSubmit={onSubmit}
        validationSchema={validationSchema(t)}
    >
        {({ errors, values, setFieldValue }) => {
            const hasErrors = Object.values(errors).some(error => !!error)
            const areInitialValues = isEqual(values, initialValues)

            return <Styled.Form>
                <ScrollableLayout
                    className={className}
                    content={
                        <Styled.FormContent>
                            <Styled.TaskTypeContainer>
                                <Styled.TaskTypeSelect
                                    id="taskType-select"
                                    name={`taskType`}
                                    value={values.taskType ?? ""}
                                    label={t(I18N_NAMESPACE, "forms.roadworkTaskForm.task")}
                                    MenuProps={{ sx: { maxWidth: 475 } }}
                                    onChange={(value: string) => handleInputChange("taskType", value, values)}
                                    onClose={() => setRemainingTasksDisplayed(false)}
                                >
                                    {taskTypesOptions.projectTasks}
                                    {taskTypesOptions.remainingTasks.length > 0 && <Divider />}
                                    {taskTypesOptions.remainingTasks.length > 0 && (remainingTasksDisplayed ?
                                        <Styled.InfoContainer key="need-reprog" severity="info">
                                            <InfoIcon color="info" />
                                            <Styled.InfoText variant="Desktop & Mobile/Body/Small" id={"no-l-task"} key={"no-l-task"}>
                                                {t(I18N_NAMESPACE, "forms.roadworkTaskForm.needNewConfig")}
                                            </Styled.InfoText>
                                        </Styled.InfoContainer>
                                        :
                                        <Styled.MoreTasksContainer>
                                            <L2RButton variant="text" onClick={(e: any) => {
                                                e.stopPropagation()
                                                setRemainingTasksDisplayed(true)
                                            }}>
                                                <Typography variant="Desktop & Mobile/Body/Small">
                                                    {t(I18N_NAMESPACE, "forms.roadworkTaskForm.viewOtherTasks")}
                                                </Typography>
                                            </L2RButton>
                                        </Styled.MoreTasksContainer>)
                                    }
                                    {taskTypesOptions.remainingTasks}
                                </Styled.TaskTypeSelect>
                                <TaskImpactBadge id="taskImpact-badge" impact={tasksInfo.find(ti => {
                                    return ti.code === values.taskType
                                })?.impact} variant="outlined" />
                            </Styled.TaskTypeContainer>
                            <Styled.QuantitySelect
                                alwaysDisplayError
                                id="quantity-input"
                                name={"quantity"}
                                type="text"
                                label={t(I18N_NAMESPACE, "forms.roadworkTaskForm.quantity")}
                                iconEnd={
                                    <Styled.UnitSelect
                                        id="unit-select"
                                        name={"unit"}
                                        disableClearable
                                        freeSolo
                                        openOnFocus
                                        forcePopupIcon
                                        autoSelect
                                        options={roadworksQuantityUnits(t)}
                                        renderInput={(params: AutocompleteRenderInputParams) => (
                                            <Styled.TextFieldInput
                                                {...params}
                                                variant="outlined"
                                                onBlur={(e: React.FocusEvent<HTMLInputElement>) => handleUnitChange(e.target.value, values)}
                                                inputProps={{
                                                    ...params.inputProps,
                                                    maxLength: 10,
                                                }} />
                                        )}
                                        sx={{ border: 0 }}
                                    >
                                    </Styled.UnitSelect>
                                }
                                onBlur={(e) => handleNumberValueChange("quantity", e.target.value, values, "0.00", setFieldValue)}
                                onChange={(e) => {
                                    const input = e.target
                                    const cursorPosition = input.selectionStart
                                    const sanitizedValue = input.value.replace(/[^0-9,.]/g, "")
                                    setFieldValue("quantity", sanitizedValue)
                                    requestAnimationFrame(() => {
                                        input.setSelectionRange(cursorPosition, cursorPosition)
                                    })
                                }}
                                value={values.quantity}
                            />
                            <Styled.FormikTextField
                                alwaysDisplayError
                                id="cost-input"
                                name="cost"
                                type="string"
                                label={t(I18N_NAMESPACE, "forms.roadworkTaskForm.cost")}
                                iconEnd="€"
                                onBlur={(e) => handleNumberValueChange("cost", e.target.value, values, "0.00", setFieldValue)}
                                onChange={(e) => {
                                    const input = e.target
                                    const cursorPosition = input.selectionStart
                                    const sanitizedValue = input.value.replace(/[^0-9,.]/g, "")
                                    setFieldValue("cost", sanitizedValue)
                                    requestAnimationFrame(() => {
                                        input.setSelectionRange(cursorPosition, cursorPosition)
                                    })
                                }}
                                value={values.cost}
                            />
                            <Styled.FormikTextField
                                name={"comment"}
                                id="comment-input"
                                label={t(I18N_NAMESPACE, "forms.roadworkTaskForm.comment")}
                                multiline
                                rows={2}
                                onBlur={(e: React.FocusEvent<HTMLInputElement>) => handleInputChange("comment", e.target.value, values)}
                            />
                        </Styled.FormContent>
                    }
                    buttons={
                        <>
                            {
                                <L2RButton
                                    id="cancel-task-edit-button"
                                    disabled={isSubmitting}
                                    onClick={onCancel}
                                    variant="tertiary"
                                >
                                    {t(I18N_NAMESPACE, "forms.roadworkTaskForm.cancel")}
                                </L2RButton>
                            }
                            <L2RButton
                                disabled={!values.taskType || hasErrors || areInitialValues || isSubmitting}
                                id="submit-task-edit-button"
                                type="submit">
                                {t(I18N_NAMESPACE, "forms.roadworkTaskForm.submit")}
                            </L2RButton>
                        </>
                    }
                />
            </Styled.Form>
        }}
    </Formik >
}
